import * as React from 'react';
import { styled } from '@mui/material/styles';
import { ClickAwayListener, tooltipClasses, TooltipProps } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { BookedEvent, CalendarItemSize } from '../../BookingsCalendarTypes';

type BookedEventItemProps = {
    event: BookedEvent;
    formattedStartTime: string;
    formattedEndTime: string;
    eventStartIndex: number;
    eventEndIndex: number;
    isOnFirstRow: boolean;
    hasNewBookingInCalendar: boolean;
    isActiveRow: boolean;
};

const bookedEventStyle = (
    eventStartIndex,
    eventEndIndex,
    isOnFirstRow,
    hasNewBookingInCalendar,
    isActiveRow
) => {
    return {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        position: 'absolute',
        gridColumnStart: eventStartIndex,
        gridColumnEnd: eventEndIndex,
        backgroundColor: '#333d45',
        height:
            (hasNewBookingInCalendar && isOnFirstRow) || (isActiveRow && isOnFirstRow)
                ? CalendarItemSize.firstRowLarge
                : hasNewBookingInCalendar || isActiveRow
                ? CalendarItemSize.large
                : CalendarItemSize.medium,
        borderRadius: '2px',
        zIndex: 3,
        padding: '8px',
        top:
            isOnFirstRow && hasNewBookingInCalendar
                ? '0.32rem'
                : isOnFirstRow
                ? '0.25rem'
                : '0.20rem',
        left: 0,
        bottom: '1px',
        right: 0,
        opacity: '0.9',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        cursor: 'pointer'
    };
};

const textStyle = (fontWeight: number) => {
    return {
        color: 'white',
        fontFamily: 'Gilroy',
        fontWeight: fontWeight,
        fontSize: '11px'
    };
};

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        display: 'flex',
        justifyContent: 'center',
        margin: 'auto',
        backgroundColor: '#333d45',
        color: 'white',
        maxWidth: 200,
        fontSize: theme.typography.pxToRem(10),
        padding: '8px',
        border: '1px solid #9E9E9E'
    }
}));

const TimelineCalendarBlockedEventItem = ({
    event,
    formattedStartTime,
    formattedEndTime,
    eventStartIndex,
    eventEndIndex,
    isOnFirstRow,
    hasNewBookingInCalendar,
    isActiveRow
}: BookedEventItemProps) => {
    const [openTooltip, setOpenTooltip] = React.useState<boolean>(false);

    const handleTooltipClose = () => {
        setOpenTooltip(false);
    };

    const handleToggleTooltip = () => {
        setOpenTooltip((currentState) => !currentState);
    };
    return (
        <ClickAwayListener onClickAway={handleTooltipClose}>
            <div>
                <StyledTooltip
                    PopperProps={{
                        disablePortal: true
                    }}
                    onClose={handleTooltipClose}
                    open={openTooltip}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    placement="right"
                    sx={{ opacity: '0.8' }}
                    title={
                        <Box>
                            <Typography>{event.title}</Typography>
                            <Typography>
                                {formattedStartTime} - {formattedEndTime}
                            </Typography>
                            <Typography>{event.product}</Typography>
                        </Box>
                    }
                >
                    <Box
                        key={event.id}
                        onClick={handleToggleTooltip}
                        sx={bookedEventStyle(
                            eventStartIndex,
                            eventEndIndex,
                            isOnFirstRow,
                            hasNewBookingInCalendar,
                            isActiveRow
                        )}
                    >
                        <Typography sx={textStyle(500)}>{event.title}</Typography>
                        <Typography sx={textStyle(400)}>
                            {formattedStartTime} - {formattedEndTime}
                        </Typography>
                        <Typography sx={textStyle(400)}>{event.product}</Typography>
                    </Box>
                </StyledTooltip>
            </div>
        </ClickAwayListener>
    );
};

export default TimelineCalendarBlockedEventItem;
